import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { Table, usePagination } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { useGetAllHotelIntegrations } from '@frontend/features/hotel-integrations/queries';
import { formatISOStringToFriendlyDate } from '@frontend/features/common/helper';
import { CreateHotelIntegrationModal } from 'src/features/hotel-integrations/components/CreateHotelIntegrationModal';
import { EditHotelIntegrationModal } from '@frontend/features/hotel-integrations/components/EditHotelIntegrationModal';

export const HotelIntegrationsList = () => {
  const { t } = useTranslation();
  const pagination = usePagination();
  const theme = useTheme();
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<string | undefined>();
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);

  const handleOpenEditModal = (id: string) => {
    setEditId(id);
    setIsOpenEdit(true);
  };

  const handleCloseEditModal = () => {
    setEditId(undefined);
    setIsOpenEdit(false);
  };

  const toggleCreateModal = () => setIsOpenCreate((prevState) => !prevState);

  const { data, isLoading } = useGetAllHotelIntegrations({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
  });

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <ListLayout title={t('hotelIntegrations.title')} subtitle={t('hotelIntegrations.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.count || 0} {t('hotelIntegrations.results')}
        </Typography>

        <SearchInput placeholder={t('hotelIntegrations.search')} onSearch={handleOnSearch} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: theme.spacing(5),
            '& button': {
              padding: theme.spacing(1),
              width: theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={toggleCreateModal} value="add" aria-label="right aligned">
            <AddIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <CreateHotelIntegrationModal open={isOpenCreate} onClose={toggleCreateModal} />
      {editId && (
        <EditHotelIntegrationModal hotelIntegrationId={editId} open={isOpenEdit} onClose={handleCloseEditModal} />
      )}

      {data?.count !== undefined && data.count > 0 && (
        <Table
          columns={[
            {
              id: 'provider',
              label: t('hotelIntegrations.table.provider'),
              align: 'left',
            },
            {
              id: 'ownerClientName',
              label: t('hotelIntegrations.table.ownerClientName'),
              align: 'left',
            },
            {
              id: 'isShared',
              label: t('hotelIntegrations.table.isShared'),
              align: 'left',
              format: (value) => <>{value ? t('common.yes') : t('common.no')}</>,
            },
            {
              id: 'createdAt',
              label: t('hotelIntegrations.table.createdAt'),
              align: 'left',
            },
            { id: 'actions', label: '', align: 'left' },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              provider: item.provider,
              ownerClientName: item.ownerClientName,
              isShared: item.isShared,
              createdAt: formatISOStringToFriendlyDate(item.createdAt),
              actions: (
                <Button onClick={() => handleOpenEditModal(item.id)}>
                  <EditIcon color="primary" />
                </Button>
              ),
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
